import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CalculateIcon from '@mui/icons-material/Calculate';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import ScienceIcon from '@mui/icons-material/Science';
import PageHeader from '../../components/common/PageHeader';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import FormSkeleton from '../../components/common/FormSkeleton';
import CvssCalculator from '../../components/common/CvssCalculator';
import TipTapEditor, { TipTapEditorHandle, EditImageHandler, EditImageResult } from '../../components/common/TipTapEditor';
import { parseWordImageSize, type WordFindingLeadChrome, type WordImageSize } from '../../components/common/wordPageLayout';
import ScreenshotGallery from '../../components/common/ScreenshotGallery';
import ImageAnnotationEditor from '../../components/common/image-editor/ImageAnnotationEditor';
import { bumpImageVersion } from '../../utils/imageVersionStore';
import SandboxImportDialog from './SandboxImportDialog';
import { sandboxService } from '../../services/sandboxService';
import { useNotification } from '../../hooks/NotificationContext';
import { CweEntry, FileEntity, Risikoszenario, SandboxFinding, SandboxFindingPayload, Schwachstellenklasse, Tool } from '../../types';
import { extractApiError, extractFieldError } from '../../utils/errorUtils';
import { validateIpAdresse } from '../../utils/ipAdresse';
import { formatSeverityLower, getCvssSeverityLabelLower, getKritikalitaetHexColor } from '../../utils/severity';

interface Props {
  /** restricted = eingeschraenkte Sitzung (/sandbox), full = Hauptaccount (/isolierte-umgebung) */
  mode: 'restricted' | 'full';
}

interface FormState {
  titel: string;
  kritikalitaet: string;
  cvss31_score: number | null;
  cvss31_vector: string;
  host: string;
  ip_adresse: string;
  schwachstellenklasse_id: number | '';
  cwe_id: number | null;
  allgemeine_beschreibung: string;
  detailbeschreibung: string;
  mitigationsmassnahmen: string;
  tool_ids: number[];
  risikoszenario_ids: number[];
}

const EMPTY: FormState = {
  titel: '',
  kritikalitaet: '',
  cvss31_score: null,
  cvss31_vector: '',
  host: '',
  ip_adresse: '',
  schwachstellenklasse_id: '',
  cwe_id: null,
  allgemeine_beschreibung: '',
  detailbeschreibung: '',
  mitigationsmassnahmen: '',
  tool_ids: [],
  risikoszenario_ids: [],
};

const fromFinding = (f: SandboxFinding): FormState => ({
  titel: f.titel || '',
  kritikalitaet: f.kritikalitaet || '',
  cvss31_score: f.cvss31_score ?? null,
  cvss31_vector: f.cvss31_vector || '',
  host: f.host || '',
  ip_adresse: f.ip_adresse || '',
  schwachstellenklasse_id: f.schwachstellenklasse_id ?? '',
  cwe_id: f.cwe_id ?? null,
  allgemeine_beschreibung: f.allgemeine_beschreibung || '',
  detailbeschreibung: f.detailbeschreibung || '',
  mitigationsmassnahmen: f.mitigationsmassnahmen || '',
  tool_ids: (f.tools || []).map((t) => t.id),
  risikoszenario_ids: (f.risikoszenarien || []).map((r) => r.id),
});

const toPayload = (s: FormState): SandboxFindingPayload => ({
  titel: s.titel.trim(),
  kritikalitaet: s.kritikalitaet || null,
  cvss31_score: s.cvss31_score,
  cvss31_vector: s.cvss31_vector || null,
  host: s.host || null,
  ip_adresse: s.ip_adresse || null,
  schwachstellenklasse_id: s.schwachstellenklasse_id === '' ? null : s.schwachstellenklasse_id,
  cwe_id: s.cwe_id,
  allgemeine_beschreibung: s.allgemeine_beschreibung || null,
  detailbeschreibung: s.detailbeschreibung || null,
  mitigationsmassnahmen: s.mitigationsmassnahmen || null,
  tool_ids: s.tool_ids,
  risikoszenario_ids: s.risikoszenario_ids,
});

const isImageFile = (filename: string) =>
  ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(filename.split('.').pop()?.toLowerCase() || '');

/**
 * Formular fuer ein Finding in der isolierten Umgebung.
 * Gleiche Inhaltsfelder, derselbe Bild-Editor und derselbe Fokusmodus wie ein Audit-Finding,
 * aber ohne Lock/Review/Bounty/Kommentare/KI.
 */
const SandboxFindingPage: React.FC<Props> = ({ mode }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { showSuccess, showError } = useNotification();

  const isNew = !id || id === 'neu';
  const findingId = isNew ? null : parseInt(id as string, 10);
  const basePath = mode === 'restricted' ? '/sandbox' : '/isolierte-umgebung';

  const [finding, setFinding] = useState<SandboxFinding | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ipAdresseError, setIpAdresseError] = useState<string | null>(null);

  const [klassen, setKlassen] = useState<Schwachstellenklasse[]>([]);
  const [tools, setTools] = useState<Tool[]>([]);
  const [risikoszenarien, setRisikoszenarien] = useState<Risikoszenario[]>([]);
  const [kritikalitaeten, setKritikalitaeten] = useState<string[]>([]);
  const [cweOptions, setCweOptions] = useState<CweEntry[]>([]);
  const [cweSearch, setCweSearch] = useState('');
  const [cweLoading, setCweLoading] = useState(false);
  const [selectedCwe, setSelectedCwe] = useState<CweEntry | null>(null);

  const [cvssOpen, setCvssOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const allgemeinRef = useRef<TipTapEditorHandle>(null);
  const detailRef = useRef<TipTapEditorHandle>(null);
  const mitigationRef = useRef<TipTapEditorHandle>(null);
  const activeEditorRef = useRef<TipTapEditorHandle | null>(null);
  const [galleryImages, setGalleryImages] = useState<FileEntity[]>([]);
  const galleryImagesRef = useRef<FileEntity[]>([]);
  const [galleryRefreshToken, setGalleryRefreshToken] = useState(0);
  const [imageEditorState, setImageEditorState] = useState<{
    file: FileEntity;
    files: FileEntity[];
    canInsert: boolean;
    wordSize?: WordImageSize;
  } | null>(null);
  const imageEditorResolveRef = useRef<((result: EditImageResult) => void) | null>(null);

  const archived = !!finding?.archiviert_am;
  const editable = !archived;
  const fileApi = sandboxService.fileApi;

  useEffect(() => {
    galleryImagesRef.current = galleryImages;
  }, [galleryImages]);

  useEffect(() => {
    Promise.all([
      sandboxService.lookups.schwachstellenklassen(),
      sandboxService.lookups.tools(),
      sandboxService.lookups.risikoszenarien(),
      sandboxService.lookups.kritikalitaeten(),
    ])
      .then(([k, t, r, kr]) => {
        setKlassen(k);
        setTools(t);
        setRisikoszenarien(r);
        setKritikalitaeten(kr);
      })
      .catch(() => setError('Referenzdaten konnten nicht geladen werden'));
  }, []);

  const load = useCallback(async () => {
    if (!findingId) return;
    setLoading(true);
    try {
      const data = await sandboxService.get(findingId);
      setFinding(data);
      setForm(fromFinding(data));
      setSelectedCwe(data.cwe ?? null);
      setError(null);
    } catch (e) {
      setError(extractApiError(e, 'Finding nicht gefunden'));
    } finally {
      setLoading(false);
    }
  }, [findingId]);

  useEffect(() => {
    if (isNew) {
      setFinding(null);
      setForm(EMPTY);
      setSelectedCwe(null);
      setLoading(false);
    } else {
      load();
    }
  }, [isNew, load]);

  useEffect(() => {
    if (!cweSearch) {
      setCweOptions([]);
      return;
    }
    let active = true;
    setCweLoading(true);
    sandboxService.lookups
      .cwe(cweSearch, 30)
      .then((rows) => active && setCweOptions(rows))
      .catch(() => active && setCweOptions([]))
      .finally(() => active && setCweLoading(false));
    return () => {
      active = false;
    };
  }, [cweSearch]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (key === 'ip_adresse') {
      setIpAdresseError(validateIpAdresse(typeof value === 'string' ? value : ''));
    }
  };

  const handleSave = async () => {
    if (!form.titel.trim()) {
      showError('Bitte einen Titel angeben');
      return;
    }
    const ipError = validateIpAdresse(form.ip_adresse);
    if (ipError) {
      setIpAdresseError(ipError);
      showError(ipError);
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const created = await sandboxService.create(toPayload(form));
        showSuccess('Finding in der isolierten Umgebung angelegt');
        window.dispatchEvent(new Event('sandbox-findings-changed'));
        navigate(`${basePath}/findings/${created.id}`, { replace: true });
      } else if (findingId) {
        const updated = await sandboxService.update(findingId, toPayload(form));
        setFinding(updated);
        setForm(fromFinding(updated));
        showSuccess('Finding gespeichert');
        window.dispatchEvent(new Event('sandbox-findings-changed'));
      }
    } catch (e) {
      const fieldError = extractFieldError(e, 'ip_adresse');
      if (fieldError) setIpAdresseError(fieldError);
      showError(extractApiError(e, 'Speichern fehlgeschlagen'));
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!findingId) return;
    try {
      const updated = await sandboxService.archive(findingId);
      setFinding(updated);
      showSuccess('Finding archiviert');
      window.dispatchEvent(new Event('sandbox-findings-changed'));
      setArchiveOpen(false);
      if (mode === 'restricted') navigate('/sandbox');
    } catch (e) {
      showError(extractApiError(e, 'Archivieren fehlgeschlagen'));
    }
  };

  const handleRestore = async () => {
    if (!findingId) return;
    try {
      const updated = await sandboxService.restore(findingId);
      setFinding(updated);
      showSuccess('Finding wiederhergestellt');
      window.dispatchEvent(new Event('sandbox-findings-changed'));
    } catch (e) {
      showError(extractApiError(e, 'Wiederherstellen fehlgeschlagen'));
    }
  };

  const handleDelete = async () => {
    if (!findingId) return;
    try {
      await sandboxService.delete(findingId);
      showSuccess('Finding endgültig gelöscht');
      window.dispatchEvent(new Event('sandbox-findings-changed'));
      navigate(basePath);
    } catch (e) {
      showError(extractApiError(e, 'Löschen fehlgeschlagen'));
    }
  };

  const handleInsertImage = useCallback(
    (fileId: number, wordSize?: WordImageSize) => {
      const url = fileApi.getPreviewUrl(fileId);
      const size = parseWordImageSize(wordSize);
      (activeEditorRef.current ?? detailRef.current)?.insertImage(url, undefined, size);
    },
    [fileApi],
  );

  const loadSandboxFile = useCallback(async (fileId: number): Promise<FileEntity> => {
    const loaded = await sandboxService.files.get(fileId);
    return {
      id: loaded.id,
      dateiname: loaded.dateiname,
      dateipfad: '',
      typ: loaded.typ ?? undefined,
      dateigroesse: loaded.dateigroesse ?? undefined,
      erstellt_am: loaded.erstellt_am ?? undefined,
      schwachstelle_id: findingId ?? undefined,
      original_file_id: loaded.original_file_id ?? null,
      annotationen: loaded.annotationen ?? null,
    };
  }, [findingId]);

  const handleEditImage: EditImageHandler = useCallback(
    async (fileId, { allowInsert, wordSize }) => {
      let files = galleryImagesRef.current;
      let file = files.find((f) => f.id === fileId);
      try {
        if (!file) {
          file = await loadSandboxFile(fileId);
          files = [...files, file];
        }
        if (file.original_file_id && !files.some((f) => f.id === file!.original_file_id)) {
          try {
            files = [...files, await loadSandboxFile(file.original_file_id)];
          } catch {
            // Original nicht mehr vorhanden: Editor startet mit der bearbeiteten Datei als neuer Basis
          }
        }
      } catch {
        showError('Bild konnte nicht geladen werden');
        return null;
      }
      return new Promise<EditImageResult>((resolve) => {
        imageEditorResolveRef.current = resolve;
        setImageEditorState({ file: file!, files, canInsert: allowInsert, wordSize });
      });
    },
    [loadSandboxFile, showError],
  );

  const finishImageEdit = useCallback((result: EditImageResult) => {
    imageEditorResolveRef.current?.(result);
    imageEditorResolveRef.current = null;
    setImageEditorState(null);
  }, []);

  const handleImageEditorSaved = useCallback(
    (saved: FileEntity, options: { insert: boolean; createdNew: boolean; wordSize: WordImageSize }) => {
      bumpImageVersion(saved.id);
      setGalleryImages((prev) =>
        options.createdNew ? [...prev, saved] : prev.map((f) => (f.id === saved.id ? saved : f)),
      );
      setGalleryRefreshToken((t) => t + 1);
      if (options.insert) handleInsertImage(saved.id, options.wordSize);
      showSuccess(
        options.createdNew
          ? options.insert
            ? 'Bearbeitete Bildversion gespeichert und eingefügt'
            : 'Bearbeitete Bildversion gespeichert – das Original bleibt erhalten'
          : 'Bildbearbeitung aktualisiert',
      );
      finishImageEdit({ fileId: saved.id, inserted: options.insert, wordSize: options.wordSize });
    },
    [finishImageEdit, handleInsertImage, showSuccess],
  );

  const handlePasteImage = useCallback(
    async (file: File): Promise<string | null> => {
      if (!findingId) return null;
      try {
        const ent = await fileApi.upload(file, 'screenshot', undefined, undefined, findingId);
        if (isImageFile(ent.dateiname)) setGalleryImages((prev) => [...prev, ent]);
        return fileApi.getPreviewUrl(ent.id);
      } catch {
        return null;
      }
    },
    [findingId, fileApi],
  );

  const handleFilesLoaded = useCallback((files: FileEntity[]) => {
    setGalleryImages(files.filter((f) => isImageFile(f.dateiname)));
  }, []);

  const handleCvssApply = (score: number, vector: string) => {
    setForm((prev) => ({ ...prev, cvss31_score: score, cvss31_vector: vector }));
    setCvssOpen(false);
  };

  /** Wie SchwachstelleDetailPage: Finding-Kopf in die erste Fokusmodus-Seite einrechnen. Ohne Audit gilt Deutsch und die Referenz SV-001. */
  const wordFindingLead = useMemo<WordFindingLeadChrome>(() => {
    const klasse = klassen.find((k) => k.id === form.schwachstellenklasse_id);
    const title = [klasse?.name_de, form.titel].filter(Boolean).join(' \u2013 ');
    const vector = (form.cvss31_vector || '').replace(/^CVSS:3\.1\//i, '').trim();
    const score = form.cvss31_score != null ? String(form.cvss31_score) : '';
    const cwe = selectedCwe
      ? `CWE-${selectedCwe.id}: ${selectedCwe.name}`
      : finding?.cwe
        ? `CWE-${finding.cwe.id}: ${finding.cwe.name}`
        : '';
    return {
      lang: 'de',
      isFirstInReport: true,
      title,
      referenzId: 'SV-001',
      host: form.ip_adresse || '',
      domain: form.host || '',
      criticality: formatSeverityLower(form.kritikalitaet),
      cvss: [vector, score].filter(Boolean).join(' '),
      cwe,
      risks: form.risikoszenario_ids
        .map((rid) => risikoszenarien.find((r) => r.id === rid)?.name_de || '')
        .filter(Boolean)
        .join(', '),
      tools: form.tool_ids
        .map((tid) => tools.find((t) => t.id === tid)?.name || '')
        .filter(Boolean)
        .join(', '),
      commonHtml: form.allgemeine_beschreibung || '',
    };
  }, [form, klassen, risikoszenarien, tools, selectedCwe, finding]);

  const labelCellSx = useMemo(
    () => ({ width: 160, color: 'text.secondary', fontWeight: 600, fontSize: '0.8rem', border: 0, py: 1, pl: 0 }),
    [],
  );
  const valueCellSx = useMemo(() => ({ border: 0, py: 1, pr: 0 }), []);
  const inputSx = { '& .MuiInputBase-input': { fontSize: '0.9rem', p: 0 } };
  const cvssLabel = form.cvss31_score != null ? getCvssSeverityLabelLower(form.cvss31_score) : '';

  if (loading) return <FormSkeleton />;

  if (error && !finding && !isNew) {
    return (
      <Box>
        <PageHeader title="Finding" backTo={basePath} />
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const headerActions = (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {editable && (
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || !form.titel.trim()}
          data-testid="sandbox-save"
        >
          {isNew ? 'Anlegen' : 'Speichern'}
        </Button>
      )}
      {!isNew && !archived && (
        <Button variant="outlined" color="warning" startIcon={<ArchiveIcon />} onClick={() => setArchiveOpen(true)} data-testid="sandbox-archive">
          Archivieren
        </Button>
      )}
      {!isNew && mode === 'full' && archived && (
        <Button variant="outlined" startIcon={<UnarchiveIcon />} onClick={handleRestore}>
          Wiederherstellen
        </Button>
      )}
      {!isNew && mode === 'full' && (
        <>
          <Button variant="contained" color="success" startIcon={<MoveToInboxIcon />} onClick={() => setImportOpen(true)} data-testid="sandbox-import">
            In Audit importieren
          </Button>
          <Button variant="outlined" color="error" startIcon={<DeleteForeverIcon />} onClick={() => setDeleteOpen(true)}>
            Endgültig löschen
          </Button>
        </>
      )}
    </Box>
  );

  return (
    <Box>
      <PageHeader
        title={isNew ? 'Neues Finding (isolierte Umgebung)' : finding?.titel || 'Finding'}
        subtitle={
          isNew
            ? 'Dieses Finding gehört zu keinem Audit und ist nur in Ihrer isolierten Umgebung sichtbar.'
            : archived
              ? 'Archiviert – nur lesbar'
              : 'Isolierte Umgebung – kein Audit zugeordnet'
        }
        backTo={basePath}
        actions={headerActions}
      />

      <Alert
        icon={<ScienceIcon fontSize="inherit" />}
        severity={archived ? 'warning' : 'info'}
        sx={{ mb: 2 }}
      >
        {archived
          ? mode === 'restricted'
            ? 'Dieses Finding ist archiviert. Wiederherstellen oder endgültiges Löschen ist nur mit dem Hauptaccount möglich.'
            : 'Dieses Finding ist archiviert. Sie können es wiederherstellen, in ein Audit importieren oder endgültig löschen.'
          : mode === 'restricted'
            ? 'Isolierte Umgebung: Findings können hier angelegt, bearbeitet und archiviert werden. Der Import in ein Audit erfolgt später über den Hauptaccount.'
            : 'Isolierte Umgebung: Dieses Finding wurde über den eingeschränkten Zugang angelegt. Prüfen Sie es und importieren Sie es in ein Audit.'}
      </Alert>

      {/* ── Kopfdaten ── */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={labelCellSx}>Titel</TableCell>
                <TableCell sx={valueCellSx}>
                  {editable ? (
                    <TextField
                      data-testid="sandbox-field-titel"
                      variant="standard"
                      fullWidth
                      value={form.titel}
                      onChange={(e) => set('titel', e.target.value)}
                      placeholder="Titel der Schwachstelle"
                      InputProps={{ disableUnderline: true }}
                      sx={{ '& .MuiInputBase-input': { fontSize: '1rem', fontWeight: 600, p: 0 } }}
                    />
                  ) : (
                    <Typography sx={{ fontWeight: 600 }}>{form.titel}</Typography>
                  )}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={labelCellSx}>Kritikalität</TableCell>
                <TableCell sx={valueCellSx}>
                  {editable ? (
                    <Select
                      data-testid="sandbox-field-kritikalitaet"
                      variant="standard"
                      value={form.kritikalitaet}
                      onChange={(e) => set('kritikalitaet', e.target.value)}
                      displayEmpty
                      disableUnderline
                      sx={{ fontSize: '0.9rem', '& .MuiSelect-select': { py: 0 } }}
                    >
                      <MenuItem value=""><em>Keine Auswahl</em></MenuItem>
                      {kritikalitaeten.map((k) => (
                        <MenuItem key={k} value={k}>{formatSeverityLower(k)}</MenuItem>
                      ))}
                    </Select>
                  ) : form.kritikalitaet ? (
                    <Chip
                      label={formatSeverityLower(form.kritikalitaet)}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        bgcolor: `${getKritikalitaetHexColor(form.kritikalitaet)}20`,
                        color: getKritikalitaetHexColor(form.kritikalitaet),
                        border: `1px solid ${getKritikalitaetHexColor(form.kritikalitaet)}40`,
                      }}
                    />
                  ) : '–'}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={labelCellSx}>CVSS 3.1</TableCell>
                <TableCell sx={valueCellSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {form.cvss31_score != null ? (
                      <>
                        <Chip label={form.cvss31_score.toFixed(1)} size="small" color="primary" sx={{ fontWeight: 700 }} />
                        {cvssLabel && <Typography variant="body2" color="text.secondary">{cvssLabel}</Typography>}
                        {form.cvss31_vector && (
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                            {form.cvss31_vector}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">–</Typography>
                    )}
                    {editable && (
                      <Tooltip title="CVSS-Rechner">
                        <IconButton size="small" onClick={() => setCvssOpen(true)} data-testid="sandbox-cvss">
                          <CalculateIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={labelCellSx}>Schwachstellenklasse</TableCell>
                <TableCell sx={valueCellSx}>
                  {editable ? (
                    <Select
                      variant="standard"
                      value={form.schwachstellenklasse_id}
                      onChange={(e) => set('schwachstellenklasse_id', e.target.value === '' ? '' : Number(e.target.value))}
                      displayEmpty
                      disableUnderline
                      fullWidth
                      sx={{ fontSize: '0.9rem', '& .MuiSelect-select': { py: 0 } }}
                    >
                      <MenuItem value=""><em>Keine Auswahl</em></MenuItem>
                      {klassen.map((k) => (
                        <MenuItem key={k.id} value={k.id}>{k.name_de}</MenuItem>
                      ))}
                    </Select>
                  ) : (
                    klassen.find((k) => k.id === form.schwachstellenklasse_id)?.name_de || finding?.schwachstellenklasse?.name_de || '–'
                  )}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={labelCellSx}>CWE</TableCell>
                <TableCell sx={valueCellSx}>
                  {editable ? (
                    <Autocomplete
                      options={cweOptions}
                      getOptionLabel={(o) => `CWE-${o.id}: ${o.name}`}
                      value={selectedCwe}
                      onChange={(_, v) => {
                        setSelectedCwe(v);
                        set('cwe_id', v ? v.id : null);
                      }}
                      onInputChange={(_, v) => setCweSearch(v)}
                      loading={cweLoading}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      noOptionsText={cweSearch ? 'Keine CWE gefunden' : 'CWE-ID oder Name eingeben...'}
                      size="small"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          placeholder="CWE-ID oder Name..."
                          InputProps={{
                            ...params.InputProps,
                            disableUnderline: true,
                            endAdornment: (
                              <>
                                {cweLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          sx={inputSx}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>CWE-{option.id}: {option.name}</Typography>
                            {option.description && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {option.description}
                              </Typography>
                            )}
                          </Box>
                        </li>
                      )}
                    />
                  ) : selectedCwe ? `CWE-${selectedCwe.id}: ${selectedCwe.name}` : '–'}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={labelCellSx}>Host</TableCell>
                <TableCell sx={valueCellSx}>
                  {editable ? (
                    <TextField variant="standard" fullWidth value={form.host} onChange={(e) => set('host', e.target.value)} placeholder="https://..." InputProps={{ disableUnderline: true }} sx={inputSx} />
                  ) : form.host || '–'}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={labelCellSx}>IP-Adresse</TableCell>
                <TableCell sx={valueCellSx}>
                  {editable ? (
                    <TextField
                      variant="standard"
                      fullWidth
                      value={form.ip_adresse}
                      onChange={(e) => set('ip_adresse', e.target.value)}
                      error={Boolean(ipAdresseError)}
                      helperText={ipAdresseError || undefined}
                      placeholder="z. B. 10.0.0.1, 10.0.0.2"
                      InputProps={{ disableUnderline: true }}
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.9rem', fontFamily: 'monospace', p: 0 } }}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{form.ip_adresse || '–'}</Typography>
                  )}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={labelCellSx}>Risikoszenarien</TableCell>
                <TableCell sx={valueCellSx}>
                  {editable ? (
                    <Select
                      variant="standard"
                      multiple
                      disableUnderline
                      fullWidth
                      value={form.risikoszenario_ids}
                      onChange={(e) => set('risikoszenario_ids', e.target.value as number[])}
                      renderValue={(sel) => (sel as number[]).map((sid) => risikoszenarien.find((r) => r.id === sid)?.name_de).filter(Boolean).join(', ') || <em>Keine Auswahl</em>}
                      displayEmpty
                      sx={{ fontSize: '0.9rem', '& .MuiSelect-select': { py: 0 } }}
                    >
                      {risikoszenarien.map((r) => (
                        <MenuItem key={r.id} value={r.id}>
                          <Checkbox size="small" checked={form.risikoszenario_ids.includes(r.id)} />
                          <ListItemText primary={r.name_de} />
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    form.risikoszenario_ids.map((sid) => risikoszenarien.find((r) => r.id === sid)?.name_de).filter(Boolean).join(', ') || '–'
                  )}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={labelCellSx}>Tools</TableCell>
                <TableCell sx={valueCellSx}>
                  {editable ? (
                    <Select
                      variant="standard"
                      multiple
                      disableUnderline
                      fullWidth
                      value={form.tool_ids}
                      onChange={(e) => set('tool_ids', e.target.value as number[])}
                      renderValue={(sel) => (sel as number[]).map((tid) => tools.find((t) => t.id === tid)?.name).filter(Boolean).join(', ') || <em>Keine Auswahl</em>}
                      displayEmpty
                      sx={{ fontSize: '0.9rem', '& .MuiSelect-select': { py: 0 } }}
                    >
                      {tools.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          <Checkbox size="small" checked={form.tool_ids.includes(t.id)} />
                          <ListItemText primary={t.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    form.tool_ids.map((tid) => tools.find((t) => t.id === tid)?.name).filter(Boolean).join(', ') || '–'
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {isNew && (
        <Alert severity="info" variant="outlined" sx={{ mb: 3 }}>
          Screenshots können hochgeladen werden, sobald das Finding angelegt wurde.
        </Alert>
      )}

      {/* ── Textfelder ── */}
      <Card sx={{ mb: 3, overflow: 'visible' }}>
        <CardContent>
          <TipTapEditor
            ref={editable ? allgemeinRef : undefined}
            testId="editor-allgemein"
            label="Allgemeine Beschreibung"
            content={form.allgemeine_beschreibung}
            onChange={editable ? (html) => set('allgemeine_beschreibung', html) : undefined}
            editable={editable}
            placeholder={editable ? 'Allgemeine Beschreibung der Schwachstelle...' : undefined}
            minHeight={editable ? 150 : 80}
            onFocus={editable ? () => { activeEditorRef.current = allgemeinRef.current; } : undefined}
            onInsertImageClick={editable ? () => { activeEditorRef.current = allgemeinRef.current; } : undefined}
            galleryImages={galleryImages}
            onInsertImageFromGallery={editable ? handleInsertImage : undefined}
            onPasteImage={editable && findingId ? handlePasteImage : undefined}
            onEditImage={editable && findingId ? handleEditImage : undefined}
            fileApi={fileApi}
          />
        </CardContent>
      </Card>

      <Card sx={{ mb: 3, overflow: 'visible' }}>
        <CardContent>
          <TipTapEditor
            ref={editable ? detailRef : undefined}
            testId="editor-detail"
            label="Detailbeschreibung"
            content={form.detailbeschreibung}
            onChange={editable ? (html) => set('detailbeschreibung', html) : undefined}
            editable={editable}
            placeholder={editable ? 'Detaillierte Beschreibung der Schwachstelle...' : undefined}
            minHeight={editable ? 'max(500px, calc(100vh - 360px))' : 80}
            wordFindingLead={wordFindingLead}
            onFocus={editable ? () => { activeEditorRef.current = detailRef.current; } : undefined}
            onInsertImageClick={editable ? () => { activeEditorRef.current = detailRef.current; } : undefined}
            galleryImages={galleryImages}
            onInsertImageFromGallery={editable ? handleInsertImage : undefined}
            onPasteImage={editable && findingId ? handlePasteImage : undefined}
            onEditImage={editable && findingId ? handleEditImage : undefined}
            fileApi={fileApi}
          />
        </CardContent>
      </Card>

      <Card sx={{ mb: 3, overflow: 'visible' }}>
        <CardContent>
          <TipTapEditor
            ref={editable ? mitigationRef : undefined}
            testId="editor-mitigation"
            label="Mitigationsmassnahmen"
            content={form.mitigationsmassnahmen}
            onChange={editable ? (html) => set('mitigationsmassnahmen', html) : undefined}
            editable={editable}
            placeholder={editable ? 'Empfohlene Mitigationsmassnahmen...' : undefined}
            minHeight={editable ? 150 : 80}
            onFocus={editable ? () => { activeEditorRef.current = mitigationRef.current; } : undefined}
            onInsertImageClick={editable ? () => { activeEditorRef.current = mitigationRef.current; } : undefined}
            galleryImages={galleryImages}
            onInsertImageFromGallery={editable ? handleInsertImage : undefined}
            onPasteImage={editable && findingId ? handlePasteImage : undefined}
            onEditImage={editable && findingId ? handleEditImage : undefined}
            fileApi={fileApi}
          />
        </CardContent>
      </Card>

      {!isNew && (
        <ScreenshotGallery
          schwachstelleId={findingId}
          onInsertImage={editable ? handleInsertImage : undefined}
          editable={editable}
          onFilesLoaded={handleFilesLoaded}
          referenceHtml={[form.allgemeine_beschreibung, form.detailbeschreibung, form.mitigationsmassnahmen].filter(Boolean).join(' ')}
          fileApi={fileApi}
          allowDelete={editable}
          onEditImage={editable ? handleEditImage : undefined}
          refreshToken={galleryRefreshToken}
        />
      )}

      <ImageAnnotationEditor
        open={imageEditorState !== null}
        file={imageEditorState?.file ?? null}
        files={imageEditorState?.files ?? []}
        fileApi={fileApi}
        canInsert={Boolean(imageEditorState?.canInsert)}
        initialWordSize={imageEditorState?.wordSize}
        onSaved={handleImageEditorSaved}
        onClose={() => finishImageEdit(null)}
      />

      <Divider sx={{ my: 3, borderColor: alpha(theme.palette.divider, isDark ? 0.6 : 1) }} />

      <CvssCalculator open={cvssOpen} onClose={() => setCvssOpen(false)} onApply={handleCvssApply} initialVector={form.cvss31_vector} />

      <ConfirmDialog
        open={archiveOpen}
        title="Finding archivieren"
        message="Das Finding wird archiviert und kann in der isolierten Umgebung nicht mehr bearbeitet werden. Wiederherstellen oder endgültiges Löschen ist nur mit dem Hauptaccount möglich."
        confirmText="Archivieren"
        onConfirm={handleArchive}
        onCancel={() => setArchiveOpen(false)}
      />

      <ConfirmDialog
        open={deleteOpen}
        title="Finding endgültig löschen"
        message="Das Finding und alle zugehörigen Dateien werden unwiderruflich gelöscht."
        confirmText="Endgültig löschen"
        onConfirm={handleDelete}
        onCancel={() => setDeleteOpen(false)}
      />

      {mode === 'full' && findingId && (
        <SandboxImportDialog
          open={importOpen}
          findings={findingId ? [{ id: findingId, titel: finding?.titel || '' }] : []}
          onClose={() => setImportOpen(false)}
          onImported={(res) => {
            setImportOpen(false);
            showSuccess(`Finding in "${res.audit_name}" importiert`);
            window.dispatchEvent(new Event('sandbox-findings-changed'));
            navigate(`/schwachstellen/${res.schwachstelle_id}`);
          }}
        />
      )}
    </Box>
  );
};

export default SandboxFindingPage;
